import { create } from "zustand";
import { type JsxElement, JsxElementType } from "@/utils/jsx/jsxElementTypes";
import type { NavTreeNode } from "@/utils/types";

interface PreviewGeneratorState {
  completed: Record<string, true>;
  queue: JsxElement[];
  imgRecieved: boolean;
  imgData: { light: Blob; dark: Blob; width: number; height: number } | null;
  domRecieved: boolean;
  domData: { outerHtml: string; navTree: NavTreeNode | null } | null;
  refreshKey: number;
}

interface PreviewGeneratorStore extends PreviewGeneratorState {
  queueElements: (elements: JsxElement[], force?: boolean) => void;
  setImgReceived: (
    data: { light: Blob; dark: Blob; width: number; height: number } | null,
  ) => void;
  setDomReceived: (
    outerHtml: string | null,
    navTree: NavTreeNode | null,
  ) => void;
  next: () => void;
  retry: () => void;
}

export const usePreviewGeneratorStore = create<PreviewGeneratorStore>(
  (set) => ({
    completed: {},
    queue: [],
    imgRecieved: false,
    imgData: null,
    domRecieved: false,
    domData: null,
    refreshKey: 0,

    queueElements: (elements, force = false) => {
      set((state) => {
        const queuedIds = new Set(
          state.queue.map((element) => element.codebaseId),
        );
        const unqueuedItems = (
          force
            ? elements
            : elements.filter(
                (element) =>
                  !state.completed[element.codebaseId] &&
                  !queuedIds.has(element.codebaseId),
              )
        ).filter(
          (element) => element.type === JsxElementType.StorybookComponent,
        );
        if (!unqueuedItems.length) {
          return state;
        }
        return {
          ...state,
          queue: [...state.queue, ...unqueuedItems],
        };
      });
    },

    setImgReceived: (blob) => {
      set((state) =>
        !state.imgRecieved
          ? {
              ...state,
              imgRecieved: true,
              imgData: blob,
            }
          : state,
      );
    },

    setDomReceived: (outerHtml, navTree) => {
      set((state) => ({
        ...state,
        domRecieved: true,
        domData: { outerHtml: outerHtml || "", navTree },
      }));
    },

    next: () => {
      set((state) => {
        const [current, ...rest] = state.queue;
        return {
          ...state,
          completed: current
            ? { ...state.completed, [current.codebaseId]: true }
            : state.completed,
          queue: rest,
          imgRecieved: false,
          imgData: null,
          domRecieved: false,
          domData: null,
        };
      });
    },

    retry: () => {
      set((state) => ({
        domRecieved: false,
        domData: null,
        imgRecieved: false,
        imgData: null,
        refreshKey: state.refreshKey + 1,
      }));
    },
  }),
);
